import { format as formatDate, isValid, set } from 'date-fns';
import { Clock } from 'lucide-react';
import { forwardRef, useState, useCallback, useEffect, useMemo } from 'react';
import { cn } from '@/shared/lib/utils';
import { ButtonSize, ButtonVariant } from '@/shared/types/button.types';
import { InputVariant } from '@/shared/types/input.types';
import { TimeFormat, type ParsedTime } from '@/shared/types/time-picker.types';
import { Button } from './button';
import { Input, type InputProps } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface TimePickerProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'type'
> {
  /**
   * Current time value (Date object or ISO string)
   */
  readonly value?: Date | string | undefined;

  /**
   * Callback when time changes (receives Date object)
   */
  readonly onChange?: ((date: Date | undefined) => void) | undefined;

  /**
   * Time format (12h or 24h)
   * @default '24h'
   */
  readonly format?: TimeFormat;

  /**
   * Show error state
   */
  readonly error?: boolean;

  /**
   * Placeholder text
   */
  readonly placeholder?: string;
}

/**
 * Parse time string to hours and minutes
 */
function parseTimeString(
  timeStr: string,
  format: TimeFormat
): ParsedTime | null {
  const trimmed = timeStr.trim().toUpperCase();

  if (format === TimeFormat.Hour12) {
    // Match: HH:MM AM/PM or H:MM AM/PM
    const regex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/;
    const match = regex.exec(trimmed);
    if (!match?.[1] || !match?.[2]) return null;

    const hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);
    const period = match[3] as 'AM' | 'PM';

    // Validate ranges
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;

    return { hours, minutes, period };
  }

  // Match: HH:MM or H:MM
  const regex = /^(\d{1,2}):(\d{2})$/;
  const match = regex.exec(trimmed);
  if (!match?.[1] || !match?.[2]) return null;

  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);

  // Validate ranges
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return { hours, minutes };
}

/**
 * Convert ParsedTime to 24-hour format
 */
function to24Hour(parsed: ParsedTime): { hours: number; minutes: number } {
  if (!parsed.period) {
    return { hours: parsed.hours, minutes: parsed.minutes };
  }

  let hours = parsed.hours;
  if (parsed.period === 'AM') {
    if (hours === 12) hours = 0; // 12 AM = 00:00
  } else if (hours !== 12) {
    hours += 12; // 1 PM = 13:00, but 12 PM = 12:00
  }

  return { hours, minutes: parsed.minutes };
}

/**
 * Format Date object to time string
 */
function formatTime(date: Date, format: TimeFormat): string {
  if (!isValid(date)) return '';

  if (format === TimeFormat.Hour12) {
    return formatDate(date, 'hh:mm a'); // 02:30 PM
  } else {
    return formatDate(date, 'HH:mm'); // 14:30
  }
}

/**
 * TimePickerContent - Popover content with scrollable hour/minute selection
 */
interface TimePickerContentProps {
  readonly hours: number[];
  readonly minutes: number[];
  readonly currentHours: number;
  readonly currentMinutes: number;
  readonly format: TimeFormat;
  readonly onTimeSelect: (
    hours: number,
    minutes: number,
    period?: 'AM' | 'PM'
  ) => void;
}

function TimePickerContent({
  hours,
  minutes,
  currentHours,
  currentMinutes,
  format,
  onTimeSelect,
}: TimePickerContentProps) {
  // Calculate initial hour for 12h format
  const getInitialHour = () => {
    if (format !== TimeFormat.Hour12) return currentHours;
    if (currentHours === 0) return 12;
    if (currentHours > 12) return currentHours - 12;
    return currentHours;
  };

  const [selectedHour, setSelectedHour] = useState(getInitialHour());
  const [selectedMinute, setSelectedMinute] = useState(currentMinutes);
  const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>(
    currentHours >= 12 ? 'PM' : 'AM'
  );

  const handleConfirm = () => {
    onTimeSelect(selectedHour, selectedMinute, selectedPeriod);
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex gap-2">
        {/* Hours Column */}
        <div className="flex flex-col gap-1">
          <span className="text-center text-xs font-medium text-muted-foreground">
            Hour
          </span>
          <div className="flex h-[200px] w-[70px] flex-col gap-1 overflow-y-auto rounded-md border border-border bg-background p-1">
            {hours.map((hour) => (
              <Button
                key={hour}
                type="button"
                variant={
                  selectedHour === hour
                    ? ButtonVariant.Default
                    : ButtonVariant.Ghost
                }
                size={ButtonSize.Small}
                className="h-8 w-full justify-center text-sm"
                onClick={() => setSelectedHour(hour)}
              >
                {String(hour).padStart(2, '0')}
              </Button>
            ))}
          </div>
        </div>

        {/* Minutes Column */}
        <div className="flex flex-col gap-1">
          <span className="text-center text-xs font-medium text-muted-foreground">
            Min
          </span>
          <div className="flex h-[200px] w-[70px] flex-col gap-1 overflow-y-auto rounded-md border border-border bg-background p-1">
            {minutes.map((minute) => (
              <Button
                key={minute}
                type="button"
                variant={
                  selectedMinute === minute
                    ? ButtonVariant.Default
                    : ButtonVariant.Ghost
                }
                size={ButtonSize.Small}
                className="h-8 w-full justify-center text-sm"
                onClick={() => setSelectedMinute(minute)}
              >
                {String(minute).padStart(2, '0')}
              </Button>
            ))}
          </div>
        </div>

        {/* Period Column (12h format only) */}
        {format === TimeFormat.Hour12 && (
          <div className="flex flex-col gap-1">
            <span className="text-center text-xs font-medium text-muted-foreground">
              Period
            </span>
            <div className="flex h-[200px] w-[70px] flex-col gap-1 rounded-md border border-border bg-background p-1">
              <Button
                type="button"
                variant={
                  selectedPeriod === 'AM'
                    ? ButtonVariant.Default
                    : ButtonVariant.Ghost
                }
                size={ButtonSize.Small}
                className="h-8 w-full justify-center text-sm"
                onClick={() => setSelectedPeriod('AM')}
              >
                AM
              </Button>
              <Button
                type="button"
                variant={
                  selectedPeriod === 'PM'
                    ? ButtonVariant.Default
                    : ButtonVariant.Ghost
                }
                size={ButtonSize.Small}
                className="h-8 w-full justify-center text-sm"
                onClick={() => setSelectedPeriod('PM')}
              >
                PM
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Button */}
      <Button
        type="button"
        variant={ButtonVariant.Default}
        size={ButtonSize.Small}
        className="w-full"
        onClick={handleConfirm}
      >
        Confirm
      </Button>
    </div>
  );
}

/**
 * TimePicker component for selecting time values
 *
 * Features:
 * - 12-hour and 24-hour format support
 * - Keyboard input with validation
 * - Popover with scrollable hour/minute selection
 * - Integrates with date-fns
 * - Theme-aware and accessible
 * - Reuses Input component
 *
 * @example
 * ```tsx
 * <TimePicker
 *   value={selectedTime}
 *   onChange={setSelectedTime}
 *   format={TimeFormat.Hour12}
 * />
 * ```
 */
const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      className,
      value,
      onChange,
      format = TimeFormat.Hour24,
      error = false,
      placeholder,
      disabled = false,
      ...props
    },
    ref
  ) => {
    // Convert value to Date if needed
    let dateValue: Date | undefined;
    if (value instanceof Date) {
      dateValue = value;
    } else if (value) {
      dateValue = new Date(value);
    }

    // Internal input value (display string)
    const [inputValue, setInputValue] = useState(() => {
      if (dateValue && isValid(dateValue)) {
        return formatTime(dateValue, format);
      }
      return '';
    });

    // Popover open state
    const [isOpen, setIsOpen] = useState(false);

    // Extract hours and minutes from current value
    const currentHours =
      dateValue && isValid(dateValue) ? dateValue.getHours() : 0;
    const currentMinutes =
      dateValue && isValid(dateValue) ? dateValue.getMinutes() : 0;

    // Generate hours and minutes arrays
    const hours = useMemo(() => {
      if (format === TimeFormat.Hour12) {
        return Array.from({ length: 12 }, (_, i) => i + 1);
      }
      return Array.from({ length: 24 }, (_, i) => i);
    }, [format]);

    const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);

    // Handle time selection from popover
    const handleTimeSelect = useCallback(
      (hours: number, minutes: number, period?: 'AM' | 'PM') => {
        let finalHours = hours;

        // Convert to 24h if needed
        if (format === TimeFormat.Hour12 && period) {
          if (period === 'AM') {
            finalHours = hours === 12 ? 0 : hours;
          } else {
            finalHours = hours === 12 ? 12 : hours + 12;
          }
        }

        const now = new Date();
        const newDate = set(now, {
          hours: finalHours,
          minutes,
          seconds: 0,
          milliseconds: 0,
        });

        setInputValue(formatTime(newDate, format));
        onChange?.(newDate);
        setIsOpen(false);
      },
      [format, onChange]
    );

    // Sync with external value changes
    useEffect(() => {
      if (dateValue && isValid(dateValue)) {
        setInputValue(formatTime(dateValue, format));
      } else if (!value) {
        setInputValue('');
      }
    }, [value, dateValue, format]);

    // Handle input change (typing)
    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setInputValue(newValue);
      },
      []
    );

    // Handle blur - validate and update
    const handleBlur = useCallback(() => {
      if (!inputValue.trim()) {
        onChange?.(undefined);
        return;
      }

      const parsed = parseTimeString(inputValue, format);
      if (!parsed) {
        // Invalid time - reset to previous valid value or empty
        if (dateValue && isValid(dateValue)) {
          setInputValue(formatTime(dateValue, format));
        } else {
          setInputValue('');
        }
        return;
      }

      // Convert to 24-hour format for Date object
      const { hours, minutes } = to24Hour(parsed);

      // Create a new Date with the selected time (use today as base)
      const now = new Date();
      const newDate = set(now, { hours, minutes, seconds: 0, milliseconds: 0 });

      // Format and update
      setInputValue(formatTime(newDate, format));
      onChange?.(newDate);
    }, [inputValue, format, dateValue, onChange]);

    // Get appropriate placeholder
    const getPlaceholder = () => {
      if (placeholder) return placeholder;
      return format === TimeFormat.Hour12 ? 'HH:MM AM/PM' : 'HH:MM';
    };

    // Determine icon color based on state
    const getIconColor = () => {
      if (error) return 'text-destructive';
      if (disabled) return 'text-muted-foreground/50';
      return 'text-muted-foreground';
    };

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className="relative w-full">
          <Input
            ref={ref}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={getPlaceholder()}
            disabled={disabled}
            variant={error ? InputVariant.Error : InputVariant.Default}
            className={cn('pr-10', className)}
            aria-label="Time input"
            aria-invalid={error}
            {...props}
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="absolute right-3 top-1/2 -translate-y-1/2 disabled:cursor-not-allowed"
              aria-label="Open time picker"
            >
              <Clock
                className={cn('size-4', getIconColor())}
                aria-hidden="true"
              />
            </button>
          </PopoverTrigger>
        </div>

        <PopoverContent className="w-auto p-0" align="start">
          <TimePickerContent
            hours={hours}
            minutes={minutes}
            currentHours={currentHours}
            currentMinutes={currentMinutes}
            format={format}
            onTimeSelect={handleTimeSelect}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

TimePicker.displayName = 'TimePicker';

export { TimePicker };
export { TimeFormat } from '@/shared/types/time-picker.types';
